import * as React from 'react';
import { MasterConfigs } from '../../../interfaces/entity';
import { constants } from '../../../constants/common';
import { Button, Flex, Tag, Typography, Space, Spin, Tooltip, notification, Modal, Divider, Progress, Card } from 'antd';
import CandidateScreening from '../../../components/CandidateScreening';
import SmallProfileCard from '../../../components/ContactInfo/SmallProfileCard';
import moment from 'moment';
import DrawerComponent from '../../../components/Forms/Drawer';
import ProfileCard from '../../../components/ContactInfo/ProfileCard';
import { EditOutlined, RobotOutlined, CheckCircleOutlined, CloseCircleOutlined, TrophyOutlined, FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import CandidateDetails from '../../../views/hrm/pre-onboardig/tabs/CandidateDetails';
import { useDispatch } from 'react-redux';
import { postData } from '../../../actions/developer';
import { decryptData, encryptData } from '../../../actions/crypto';

const ViewCandidate = ({ record }) => {
  const [openDrawer, setOpenDrawer] = React.useState(false);

  if (!record) return null;

  return (
    <div>
      <div className='cursor-pointer' onClick={() => setOpenDrawer(true)} >
        <SmallProfileCard
          name={constants.GET_FULL_NAME(record)}
          avatar={record?.candidatePhotoUrl}
        />
      </div>
      <DrawerComponent
        title={
          <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
            <ProfileCard
              size={40}
              name={`${record?.firstName || ''} ${record?.lastName || ''}`}
              avatarUrl={record?.profilePhoto ? process.env.FILE_URL + record?.profilePhoto : null}
              role={record?.phone || ''}
            />
            {!record?.converted && <div className="flex gap-2">
              <Button disabled type="primary">Send Email</Button>
              <Button icon={<EditOutlined />} />
            </div>}
          </Flex>
        }
        visible={openDrawer}
        handleCancel={() => setOpenDrawer(false)}
        isView={true}
        loading={false}
        submitForm={() => { }}
        rest={{
          width: '90%', style: {
            background: '#eef1f9',
            padding: '0px !important',
          },
          bodyStyle: { padding: 0 },
          headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
        }}
      >
        <>
          <CandidateDetails candidate={record} />
        </>
      </DrawerComponent>
    </div>
  );
}

const AIScreeningButton = ({ record }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  // Initialize score and cached state from record
  const initialScore = record?.aiScreening?.overallScore || record?.aiScreening?.overallMatchPercentage || null;
  const hasCachedData = !!(record?.aiScreening && (record.aiScreening.overallScore !== null && record.aiScreening.overallScore !== undefined));

  const [score, setScore] = React.useState<number | null>(initialScore);
  const [isCached, setIsCached] = React.useState(hasCachedData);
  const [showScreeningModal, setShowScreeningModal] = React.useState(false);
  const [screeningData, setScreeningData] = React.useState<any>(null);

  // Update score when record changes
  React.useEffect(() => {
    const newScore = record?.aiScreening?.overallScore || record?.aiScreening?.overallMatchPercentage || null;
    const hasData = !!(record?.aiScreening && (record.aiScreening.overallScore !== null && record.aiScreening.overallScore !== undefined));

    if (newScore !== null) {
      setScore(newScore);
      setIsCached(hasData);
      if (hasData && record?.aiScreening) {
        setScreeningData(record.aiScreening);
      }
    }
  }, [record]);

  const handleScreenWithAI = async (forceRescreen = false) => {
    if (!record?._id) return;

    setLoading(true);
    try {
      const encryptedId = encryptData(record._id);
      const url = forceRescreen
        ? `/shortlistedCandidates/screen-with-ai/${encryptedId}?force=true`
        : `/shortlistedCandidates/screen-with-ai/${encryptedId}`;
      const resData = await dispatch(postData(url, {}));

      if (resData?.code === 200) {
        const decryptedData = decryptData(resData.data);
        const screeningResult = decryptedData?.screeningResult;

        if (screeningResult) {
          const newScore = screeningResult.overallScore || screeningResult.overallMatchPercentage;
          setScore(newScore);
          setIsCached(decryptedData?.cached || false);

          // Update screening data for modal
          setScreeningData({
            overallScore: screeningResult.overallScore || screeningResult.overallMatchPercentage,
            jdMatchScore: screeningResult.jdMatchScore,
            experienceScore: screeningResult.experienceScore,
            skillsMatchScore: screeningResult.skillsMatchScore,
            strengths: screeningResult.strengths || [],
            weaknesses: screeningResult.weaknesses || [],
            recommendation: screeningResult.recommendation,
            detailedAnalysis: screeningResult.detailedAnalysis,
            screenedAt: decryptedData?.screenedAt || new Date()
          });

          // Show modal with results
          setShowScreeningModal(true);

          if (!decryptedData?.cached) {
            notification.success({
              message: 'AI Screening Completed',
              description: 'Screening results are now available.',
              duration: 3,
            });
          }
        } else {
          throw new Error('Invalid response from AI screening');
        }
      } else {
        throw new Error(resData?.message || 'Failed to screen candidate');
      }
    } catch (error: any) {
      console.error('Error screening candidate with AI:', error);
      notification.error({
        message: 'Screening Failed',
        description: error?.message || 'Failed to screen candidate with AI. Please try again.',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#52c41a'; // green
    if (score >= 60) return '#faad14'; // orange
    return '#ff4d4f'; // red
  };

  const getRecommendationColor = (recommendation: string) => {
    if (recommendation?.toLowerCase().includes('strong match')) return '#52c41a';
    if (recommendation?.toLowerCase().includes('proceed with caution')) return '#faad14';
    return '#ff4d4f';
  };

  return (
    <>
      <Space direction="vertical" align="center" size="small">
        {score !== null ? (
          <Tooltip
            title={
              <div>
                <div>JD Match: {record?.aiScreening?.jdMatchScore || 'N/A'}/100</div>
                <div>Experience: {record?.aiScreening?.experienceScore || 'N/A'}/100</div>
                <div>Skills: {record?.aiScreening?.skillsMatchScore || 'N/A'}/100</div>
                {isCached && record?.aiScreening?.screenedAt && (
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#ccc' }}>
                    Cached: {new Date(record.aiScreening.screenedAt).toLocaleDateString()}
                  </div>
                )}
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#999' }}>
                  Click to view details
                </div>
              </div>
            }
          >
            <Tag
              color={getScoreColor(score)}
              style={{ fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
              onClick={() => {
                if (record?.aiScreening) {
                  setScreeningData(record.aiScreening);
                  setShowScreeningModal(true);
                }
              }}
            >
              {score}/100 {isCached && '📋'}
            </Tag>
          </Tooltip>
        ) : (
          <Button
            type="primary"
            icon={<RobotOutlined />}
            size="small"
            loading={loading}
            onClick={() => handleScreenWithAI(false)}
            style={{ width: '100%' }}
          >
            Screen with AI
          </Button>
        )}
      </Space>

      {/* Screening Results Modal */}
      <Modal
        title={
          <Flex align="center" gap={12}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '20px'
            }}>
              <RobotOutlined />
            </div>
            <div>
              <Typography.Title level={4} style={{ margin: 0 }}>AI Screening Results</Typography.Title>
              {isCached && screeningData?.screenedAt && (
                <Flex align="center" gap={4} style={{ marginTop: 4 }}>
                  <ClockCircleOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    Cached: {new Date(screeningData.screenedAt).toLocaleString()}
                  </Typography.Text>
                </Flex>
              )}
            </div>
          </Flex>
        }
        open={showScreeningModal}
        onCancel={() => setShowScreeningModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowScreeningModal(false)}>
            Close
          </Button>
        ]}
        width={800}
        styles={{
          body: { padding: '24px', maxHeight: '70vh', overflowY: 'auto' }
        }}
      >
        {screeningData && (
          <div>
            {/* Overall Score Card */}
            <Card
              style={{
                marginBottom: '24px',
                background: `linear-gradient(135deg, ${getScoreColor(screeningData.overallScore || screeningData.overallMatchPercentage || 0)}15 0%, ${getScoreColor(screeningData.overallScore || screeningData.overallMatchPercentage || 0)}05 100%)`,
                border: `2px solid ${getScoreColor(screeningData.overallScore || screeningData.overallMatchPercentage || 0)}40`,
                borderRadius: '12px'
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <TrophyOutlined style={{ fontSize: '48px', color: getScoreColor(screeningData.overallScore || screeningData.overallMatchPercentage || 0), marginBottom: '12px' }} />
                <Typography.Title
                  level={1}
                  style={{
                    margin: 0,
                    color: getScoreColor(screeningData.overallScore || screeningData.overallMatchPercentage || 0),
                    fontSize: '56px',
                    fontWeight: 'bold'
                  }}
                >
                  {screeningData.overallScore || screeningData.overallMatchPercentage || 0}
                  <span style={{ fontSize: '32px', fontWeight: 'normal', marginLeft: '8px' }}>/100</span>
                </Typography.Title>
                <Tag
                  color={getRecommendationColor(screeningData.recommendation || '')}
                  style={{
                    fontSize: '16px',
                    padding: '8px 20px',
                    marginTop: '12px',
                    borderRadius: '20px',
                    border: 'none',
                    fontWeight: '500'
                  }}
                >
                  {screeningData.recommendation || 'Not Recommended'}
                </Tag>
              </div>
            </Card>

            {/* Score Breakdown */}
            <Card
              title={
                <Flex align="center" gap={8}>
                  <FileTextOutlined />
                  <span>Score Breakdown</span>
                </Flex>
              }
              style={{ marginBottom: '24px', borderRadius: '12px' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Flex justify="space-between" align="center" style={{ marginBottom: '8px' }}>
                    <Typography.Text strong style={{ fontSize: '15px' }}>JD Match Score</Typography.Text>
                    <Tag
                      color={getScoreColor(screeningData.jdMatchScore || 0)}
                      style={{ fontSize: '14px', padding: '4px 12px', fontWeight: '600' }}
                    >
                      {screeningData.jdMatchScore || 0}/100
                    </Tag>
                  </Flex>
                  <Progress
                    percent={screeningData.jdMatchScore || 0}
                    strokeColor={getScoreColor(screeningData.jdMatchScore || 0)}
                    showInfo={false}
                    strokeWidth={10}
                    style={{ marginBottom: '4px' }}
                  />
                </div>
                <div>
                  <Flex justify="space-between" align="center" style={{ marginBottom: '8px' }}>
                    <Typography.Text strong style={{ fontSize: '15px' }}>Experience Match</Typography.Text>
                    <Tag
                      color={getScoreColor(screeningData.experienceScore || 0)}
                      style={{ fontSize: '14px', padding: '4px 12px', fontWeight: '600' }}
                    >
                      {screeningData.experienceScore || 0}/100
                    </Tag>
                  </Flex>
                  <Progress
                    percent={screeningData.experienceScore || 0}
                    strokeColor={getScoreColor(screeningData.experienceScore || 0)}
                    showInfo={false}
                    strokeWidth={10}
                    style={{ marginBottom: '4px' }}
                  />
                </div>
                <div>
                  <Flex justify="space-between" align="center" style={{ marginBottom: '8px' }}>
                    <Typography.Text strong style={{ fontSize: '15px' }}>Skills Match</Typography.Text>
                    <Tag
                      color={getScoreColor(screeningData.skillsMatchScore || 0)}
                      style={{ fontSize: '14px', padding: '4px 12px', fontWeight: '600' }}
                    >
                      {screeningData.skillsMatchScore || 0}/100
                    </Tag>
                  </Flex>
                  <Progress
                    percent={screeningData.skillsMatchScore || 0}
                    strokeColor={getScoreColor(screeningData.skillsMatchScore || 0)}
                    showInfo={false}
                    strokeWidth={10}
                    style={{ marginBottom: '4px' }}
                  />
                </div>
              </Space>
            </Card>

            <Flex gap={16} style={{ marginBottom: '24px' }}>
              {/* Strengths */}
              {screeningData.strengths && screeningData.strengths.length > 0 && (
                <Card
                  title={
                    <Flex align="center" gap={8}>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span style={{ color: '#52c41a' }}>Strengths</span>
                    </Flex>
                  }
                  style={{
                    flex: 1,
                    borderRadius: '12px',
                    border: '2px solid #52c41a30',
                    background: '#f6ffed'
                  }}
                  headStyle={{ background: '#f6ffed', borderBottom: '2px solid #52c41a30', borderRadius: '12px 12px 0 0' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {screeningData.strengths.map((strength: string, index: number) => (
                      <Flex key={index} align="flex-start" gap={8}>
                        <CheckCircleOutlined style={{ color: '#52c41a', marginTop: '4px', fontSize: '16px' }} />
                        <Typography.Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                          {strength}
                        </Typography.Text>
                      </Flex>
                    ))}
                  </Space>
                </Card>
              )}

              {/* Weaknesses */}
              {screeningData.weaknesses && screeningData.weaknesses.length > 0 && (
                <Card
                  title={
                    <Flex align="center" gap={8}>
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      <span style={{ color: '#ff4d4f' }}>Weaknesses</span>
                    </Flex>
                  }
                  style={{
                    flex: 1,
                    borderRadius: '12px',
                    border: '2px solid #ff4d4f30',
                    background: '#fff1f0'
                  }}
                  headStyle={{ background: '#fff1f0', borderBottom: '2px solid #ff4d4f30', borderRadius: '12px 12px 0 0' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {screeningData.weaknesses.map((weakness: string, index: number) => (
                      <Flex key={index} align="flex-start" gap={8}>
                        <CloseCircleOutlined style={{ color: '#ff4d4f', marginTop: '4px', fontSize: '16px' }} />
                        <Typography.Text style={{ fontSize: '14px', lineHeight: '1.6', color: '#ff4d4f' }}>
                          {weakness}
                        </Typography.Text>
                      </Flex>
                    ))}
                  </Space>
                </Card>
              )}
            </Flex>

            {/* Detailed Analysis */}
            {screeningData.detailedAnalysis && (
              <Card
                title={
                  <Flex align="center" gap={8}>
                    <FileTextOutlined />
                    <span>Detailed Analysis</span>
                  </Flex>
                }
                style={{ borderRadius: '12px' }}
              >
                <Typography.Paragraph
                  style={{
                    textAlign: 'justify',
                    whiteSpace: 'pre-wrap',
                    fontSize: '14px',
                    lineHeight: '1.8',
                    color: '#595959',
                    margin: 0
                  }}
                >
                  {screeningData.detailedAnalysis}
                </Typography.Paragraph>
              </Card>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};


const ShortListedConfig: MasterConfigs = {
  api: () => '/shortlistedCandidates',
  title: 'Shortlisted',
  isActionTab: false,
  searchKeys: ['firstName', 'middleName', 'lastName', 'email', 'phone'],
  modalTitle: (action) => `${action}  Candidate Information`,
  rowSelection: {
    type: 'checkbox',
    ...{
      onChange: (selectedRowKeys: React.Key[], selectedRows) => {
        console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
      },
      getCheckboxProps: (record) => ({
        name: record._id,
      }),
    }
  },
  renderComponent: () => {
    return <CandidateScreening />
  },
  getColumns: () => {
    return [
      {
        title: 'Candidate Name',
        dataIndex: '',
        key: '',
        fixed: 'left',
        width: 150,
        render: (text, record) => <ViewCandidate record={record} />,
      },
      {
        title: 'Job Role',
        dataIndex: 'profileFor',
        key: 'profileFor',
        isSearch: true,
        width: 150,
        render: (text) => text?.name,
      },
      {
        title: 'Experiance',
        dataIndex: 'experiance',
        key: 'experiance',
        isSearch: true,
        width: 110,
        align: 'center'
      },
      {
        title: 'Notice Period',
        dataIndex: 'noticePeriod',
        key: 'noticePeriod',
        width: 150,
        align: 'center'
      },
      {
        title: 'CTC',
        dataIndex: 'currentCTC',
        key: 'currentCTC',
        width: 100,
        align: 'center'
      },
      {
        title: 'E-CTC',
        dataIndex: 'expectedCTC',
        key: 'expectedCTC',
        width: 100,
        align: 'center'
      },
      {
        title: 'Department',
        dataIndex: 'department',
        key: 'department',
        width: 150,
        render: (text) => text?.name || text,
        ellipsis: true
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 200,
        render: (text) => <><Typography.Text copyable={{ text }} />  {text}</>,
        ellipsis: true
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        width: 200,
        align: 'center'
      },
      {
        title: 'Gender',
        dataIndex: 'gender',
        key: 'gender',
        width: 100,
        align: 'center',
      },
      {
        title: 'Candidate Owner',
        dataIndex: 'candidateOwner',
        key: 'candidateOwner',
        width: 200,
        render: (text) => text ? <SmallProfileCard
          name={constants.GET_FULL_NAME(text)}
          avatar={text?.avatar}
        /> : 'N/A',
      },
      {
        title: 'AI Screening',
        dataIndex: '',
        key: 'aiScreening',
        width: 150,
        fixed: 'right',
        align: 'center',
        render: (text, record) => <AIScreeningButton record={record} />
      },
      // {
      //   title: 'Status',
      //   dataIndex: 'profileStatus',
      //   key: 'profileStatus',
      //   fixed: 'right',
      //   width: 150,
      //   render: (text) => <Badge text={text} color="yellow" />
      // }
    ];
  },
  getFormColumns: () => {
    return [
      {
        label: 'First Name',
        name: 'firstName',
        type: 'text',
        rules: [{
          required: true,
          message: 'First name is required'
        }]
      },
      {
        label: 'Middle Name',
        name: 'middleName',
        type: 'text'
      },
      {
        label: 'Last Name',
        name: 'lastName',
        type: 'text'
      },
      {
        label: 'Email',
        name: 'email',
        type: 'text',
        rules: [{
          required: true,
          message: 'Field is required'
        }]
      },
      {
        label: 'Phone',
        name: 'phone',
        type: 'text',
        rules: [{
          required: true,
          message: 'Field is required'
        }]
      },
      {
        label: 'Gender',
        name: 'gender',
        rules: [{
          required: true,
          message: 'Gender is required'
        }],
        type: 'select',
        children: [
          { name: 'Male', value: 'Male' },
          { name: 'Female', value: 'Female' },
          { name: 'Other', value: 'Other' },
        ],
      },
      {
        label: 'Job Role',
        name: 'profileFor',
        rules: [{
          required: true,
          message: 'Job Role is required'
        }],
        type: 'select',
        isCombo: true
      },
      {
        label: 'Candidate Type',
        name: 'candidateType',
        rules: [{
          required: true,
          message: 'Candidate Type is required'
        }],
        type: 'select',
        children: [
          { name: 'Intern', value: 'Intern' },
          { name: 'Fresher', value: 'Fresher' },
          { name: 'Experience', value: 'Experience' },
        ],
      },
      {
        label: 'Current CTC',
        name: 'currentCTC',
        rules: [{
          required: true,
          message: 'Current CTC is required'
        }],
        inputProps: { addonBefore: 'INR' },
        type: 'text'
      },
      {
        label: 'Expected CTC',
        name: 'expectedCTC',
        inputProps: { addonBefore: 'INR' },
        rules: [{
          required: true,
          message: 'Expected CTC is required'
        }],
        type: 'text'
      },
      {
        label: 'Experiance',
        name: 'experiance',
        rules: [{
          required: true,
          message: 'Experiance is required'
        }],
        inputProps: { addonAfter: 'Years' },
        type: 'text'
      },
      {
        label: 'Notice Period',
        name: 'noticePeriod',
        inputProps: { addonAfter: 'Month' },
        type: 'text'
      },
      {
        label: 'Last Working Date',
        name: 'lastWorkingDate',
        type: 'date'
      },
      {
        label: 'Applied For',
        name: 'appliedFor',
        rules: [{
          required: true,
          message: 'Applied For is required'
        }],
        type: 'select',
        isCombo: true
      },
      {
        label: 'Resume/CV',
        name: 'resume',
        rules: [{
          required: true,
          message: 'Resume/CV is required',
        }],
        importData: {
          info: 'Extract PDF',
        },
        uploadProps: {
          maxCount: 1,
          accept: '.pdf',
          listType: 'text'
        },
        type: 'file',
      },
      {
        label: 'Reason of Leaving Previous Company',
        name: 'reasonOfLeaving',
        span: 16,
        inputTextAreaProps: {
          rows: 2
        },
        type: 'textarea',
      },
    ];
  },
  getFilterColumns: () => {
    const filterColumns: any[] = [
      {
        label: 'Email',
        name: 'email',
        type: 'text',
        span: 4,
        placeholder: 'Search by email',
      },
      {
        label: 'Job Role',
        name: 'profileFor',
        type: 'select',
        isCombo: true,
        span: 4,
        selectProps: {
          allowClear: true,
          placeholder: 'Select Job Role',
          showSearch: true,
        },
      },
      {
        label: 'Applied For',
        name: 'appliedFor',
        type: 'select',
        isCombo: true,
        span: 4,
        selectProps: {
          allowClear: true,
          placeholder: 'Select Applied For',
          showSearch: true,
        },
      },
      {
        label: 'Candidate Type',
        name: 'candidateType',
        type: 'select',
        span: 4,
        selectProps: {
          allowClear: true,
          placeholder: 'Select Candidate Type',
        },
        children: [
          { name: 'Intern', value: 'Intern' },
          { name: 'Fresher', value: 'Fresher' },
          { name: 'Experience', value: 'Experience' },
        ],
      },
      {
        label: 'Gender',
        name: 'gender',
        type: 'select',
        span: 4,
        selectProps: {
          allowClear: true,
          placeholder: 'Select Gender',
        },
        children: [
          { name: 'Male', value: 'Male' },
          { name: 'Female', value: 'Female' },
          { name: 'Other', value: 'Other' },
        ],
      },
      {
        label: 'Phone',
        name: 'phone',
        type: 'text',
        span: 4,
        placeholder: 'Search by phone',
      },
      {
        label: 'Experience',
        name: 'experiance',
        type: 'number',
        span: 4,
        inputProps: { addonAfter: 'Years' },
        placeholder: 'Enter experience in years',
      },
      {
        label: 'Notice Period',
        name: 'noticePeriod',
        type: 'number',
        span: 4,
        inputProps: { addonAfter: 'Months' },
        placeholder: 'Enter notice period in months',
      },
      {
        label: 'Current CTC',
        name: 'currentCTC',
        type: 'number',
        span: 4,
        placeholder: 'Enter current CTC',
      },
      {
        label: 'Expected CTC',
        name: 'expectedCTC',
        type: 'number',
        span: 4,
        placeholder: 'Enter expected CTC',
      },
      {
        label: 'Last Working Date',
        name: 'lastWorkingDate',
        type: 'date',
        span: 4,
        dateProps: {
          format: 'DD/MM/YYYY',
          placeholder: 'Select Last Working Date',
          allowClear: true,
        },
      },
      {
        label: 'Created Date From',
        name: 'startDate',
        type: 'date',
        span: 4,
        dateProps: {
          format: 'DD/MM/YYYY',
          placeholder: 'Start Date',
          allowClear: true,
        },
      },
      {
        label: 'Created Date To',
        name: 'endDate',
        type: 'date',
        span: 4,
        dateProps: {
          format: 'DD/MM/YYYY',
          placeholder: 'End Date',
          allowClear: true,
        },
      },
      {
        label: 'Candidate Owner',
        name: 'candidateOwner',
        type: 'select',
        isCombo: true,
        span: 4,
        selectProps: {
          allowClear: true,
          placeholder: 'Select Candidate Owner',
          showSearch: true,
        },
      },
    ];

    return filterColumns;
  },
  combo: [
    {
      name: 'profileFor',
      api: '/job-titles',
      idKey: '_id',
      render: (text) => text.name
    },
    {
      name: 'appliedFor',
      api: '/vendor/crm/current_opening',
      idKey: '_id',
      render: (text) => {
        return `AGJ${text.jobId}_${text.jobTitle?.name}_${text.workLocation}_${text.experiance}+Years`;
      },
    },
    {
      name: 'candidateOwner',
      api: '/users',
      idKey: '_id',
      render: (text) => constants.GET_FULL_NAME(text)
    },
  ],
  queryParams: {
    andOr: 'and', // Use 'and' to combine filters
  },
  actions: [
    { name: 'Add Candidate', actionType: 'Add' },
    { name: 'Return', actionType: 'Back' },
  ],
};

export default ShortListedConfig;




